import { useEffect, useState } from "react";

/* ──────────────────────────────────────────────────────────────
   Themed, promise-based replacements for window.confirm / alert.
   Usage:
     if (!(await confirmDialog({ title: "Delete?", message: "…", tone: "danger" }))) return;
     await alertDialog("Saved to the bank.");
   A single <DialogHost /> must be mounted once at the app root.
   ────────────────────────────────────────────────────────────── */

type Tone = "default" | "danger";

type DialogRequest = {
  id: number;
  kind: "confirm" | "alert";
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  tone: Tone;
  resolve: (ok: boolean) => void;
};

let current: DialogRequest | null = null;
const listeners = new Set<() => void>();
let seq = 0;

function emit() {
  for (const l of listeners) l();
}

function open(req: Omit<DialogRequest, "id">) {
  // If a dialog is somehow already open, dismiss it as cancelled first.
  if (current) current.resolve(false);
  current = { ...req, id: ++seq };
  emit();
}

function settle(ok: boolean) {
  const r = current;
  if (!r) return;
  current = null;
  emit();
  r.resolve(ok);
}

export type ConfirmOptions = {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: Tone;
};

export function confirmDialog(opts: string | ConfirmOptions): Promise<boolean> {
  const o = typeof opts === "string" ? { message: opts } : opts;
  return new Promise<boolean>((resolve) => {
    open({
      kind: "confirm",
      title: o.title ?? "Are you sure?",
      message: o.message,
      confirmLabel: o.confirmLabel ?? (o.tone === "danger" ? "Delete" : "Confirm"),
      cancelLabel: o.cancelLabel ?? "Cancel",
      tone: o.tone ?? "default",
      resolve,
    });
  });
}

export type AlertOptions = {
  title?: string;
  message: string;
  confirmLabel?: string;
  tone?: Tone;
};

export function alertDialog(opts: string | AlertOptions): Promise<void> {
  const o = typeof opts === "string" ? { message: opts } : opts;
  return new Promise<void>((resolve) => {
    open({
      kind: "alert",
      title: o.title ?? "",
      message: o.message,
      confirmLabel: o.confirmLabel ?? "OK",
      cancelLabel: "",
      tone: o.tone ?? "default",
      resolve: () => resolve(),
    });
  });
}

export function DialogHost() {
  const [, force] = useState(0);

  useEffect(() => {
    const l = () => force((n) => n + 1);
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  }, []);

  const d = current;

  useEffect(() => {
    if (!d) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") settle(false);
      else if (e.key === "Enter") settle(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [d?.id]);

  if (!d) return null;

  const danger = d.tone === "danger";
  const accent = danger ? "var(--atl-accent)" : "var(--atl-gold)";
  const accentSoft = danger ? "#fbf2f3" : "var(--atl-gold-soft)";

  return (
    <div
      className="atl"
      onClick={() => settle(false)}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        background: "rgba(43,30,20,.45)",
        display: "grid",
        placeItems: "center",
        padding: 20,
        backdropFilter: "blur(3px)",
        animation: "bk-fade-in .12s ease-out",
      }}
    >
      <div
        className="atl-card"
        onClick={(e) => e.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
        style={{
          width: "100%",
          maxWidth: 420,
          padding: 26,
          boxShadow: "0 30px 80px rgba(40,25,15,.3)",
          animation: "bk-pop-in .14s ease-out",
        }}
      >
        <div style={{ display: "flex", gap: 14 }}>
          <div
            style={{
              width: 44,
              height: 44,
              flex: "0 0 auto",
              borderRadius: 13,
              background: accentSoft,
              color: accent,
              display: "grid",
              placeItems: "center",
              fontWeight: 900,
              fontSize: 22,
              lineHeight: 1,
            }}
          >
            {danger ? "!" : "i"}
          </div>
          <div style={{ flex: 1, minWidth: 0, paddingTop: 2 }}>
            {d.title && (
              <h3 className="atl-serif" style={{ fontSize: 20, marginBottom: 6 }}>
                {d.title}
              </h3>
            )}
            <p style={{ fontSize: 14, lineHeight: 1.55, color: "var(--atl-soft)", fontWeight: 500 }}>{d.message}</p>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 24 }}>
          {d.kind === "confirm" && (
            <button className="atl-btn atl-btn--ghost" onClick={() => settle(false)}>
              {d.cancelLabel}
            </button>
          )}
          <button className="atl-btn atl-btn--primary" autoFocus onClick={() => settle(true)}>
            {d.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
